import { useEffect, useRef, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Microphone, Stop, SpeakerHigh, Trash, WaveSine } from "@phosphor-icons/react";

export default function VoiceNoteRecorder({ interventionId, notes = [], onSaved }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => stopStream(), []);

  const stopStream = () => {
    if (mediaRef.current) {
      try { mediaRef.current.stream.getTracks().forEach((t) => t.stop()); }
      catch (e) { console.debug("[voice] track stop failed:", e?.message || e); }
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stopStream();
        await upload(blob);
      };
      mediaRef.current = rec;
      rec.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (err) {
      toast.error("Microphone refusé — autorisez l'accès dans les réglages du navigateur");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    setRecording(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const upload = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "note.webm");
      fd.append("language", "fr");
      const r = await api.post(`/interventions/${interventionId}/voice-note`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.transcription ? "Transcription OK" : "Audio enregistré (sans transcription)");
      onSaved?.(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const del = async (nid) => {
    if (!window.confirm("Supprimer cette note vocale ?")) return;
    try { await api.delete(`/interventions/${interventionId}/voice-note/${nid}`); onSaved?.(null); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  // JWT lives in an HttpOnly cookie (auto-attached by the browser). Empty query
  // token is fine — `user_from_query_auth` falls back to cookie.
  const token = "";
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 space-y-2" data-testid="voice-notes">
      <div className="flex items-center gap-2">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${recording ? "bg-red-500/20 animate-pulse" : "bg-white/5"}`}>
          <Microphone size={20} weight={recording ? "fill" : "duotone"} className={recording ? "text-red-400" : "text-white/60"} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-white/50">Notes vocales · Whisper FR</div>
          <div className="text-xs text-white/70">
            {recording ? <span className="font-mono">Rec {mm}:{ss}</span> : uploading ? "Transcription…" : `${notes.length} note${notes.length > 1 ? "s" : ""}`}
          </div>
        </div>
        {recording ? (
          <button onClick={stop} className="px-3 h-10 rounded-full bg-red-500 hover:bg-red-600 text-sm font-semibold active:scale-95 flex items-center gap-1" data-testid="voice-stop">
            <Stop size={14} weight="fill" /> Stop
          </button>
        ) : (
          <button onClick={start} disabled={uploading} className="px-3 h-10 rounded-full bg-[#005CFF] hover:bg-[#0047CC] text-sm font-semibold active:scale-95 flex items-center gap-1 disabled:opacity-50" data-testid="voice-start">
            <Microphone size={14} weight="fill" /> Dicter
          </button>
        )}
      </div>
      {notes.length > 0 && (
        <div className="space-y-1.5 pt-1">
          {notes.map((n) => (
            <div key={n.id} className="rounded-lg bg-black/30 border border-white/5 p-2 space-y-1.5" data-testid={`voice-note-${n.id}`}>
              <div className="flex items-center gap-2 text-[10px] text-white/40">
                <WaveSine size={11} className="text-[#7CB2FF]" />
                <span>{n.user_name}</span>
                <span>·</span>
                <span>{(n.created_at || "").replace("T", " ").slice(0, 16)}</span>
                <button onClick={() => del(n.id)} className="ml-auto text-white/40 hover:text-red-400"><Trash size={11} /></button>
              </div>
              <audio controls preload="none" src={`${API}/interventions/${interventionId}/voice-note/${n.id}/audio?auth=${encodeURIComponent(token)}`} className="w-full h-8" />
              {n.transcription && (
                <div className="text-xs text-white/80 italic">« {n.transcription} »</div>
              )}
              {n.transcription && (
                <button
                  onClick={async () => {
                    try {
                      const r = await api.post(`/interventions/${interventionId}/voice-note/${n.id}/extract-tasks`);
                      toast.success(r.data.created > 0 ? `${r.data.created} tâche${r.data.created > 1 ? "s" : ""} créée${r.data.created > 1 ? "s" : ""}` : "Aucune action détectée");
                      onSaved?.(null);
                    } catch (err) { toast.error(formatApiError(err)); }
                  }}
                  className="text-[10px] text-[#7CB2FF] hover:text-white flex items-center gap-1 mt-1"
                  data-testid={`voice-to-tasks-${n.id}`}
                >
                  ✨ Convertir en checklist
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
